import { Loading } from '@cibo/profile'
import { ResponsiveDialog, logRequestError } from '@cibo/ui'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Button,
  Collapse,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { pluck } from 'ramda'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useDeleteProgramEngagement,
  useProgram,
  useProgramEngagement,
} from '../../../../../queries'
import { ConfirmIneligibleDialogProps } from '../ConfirmIneligibleDialogTypes'
import { FieldReasonsTable } from '../FieldReasonsTable/FieldReasonsTable'

export const ConfirmDeleteEngagementDialog = ({
  engagementId,
  open,
  reasons,
  onConfirm,
  onClose,
}: ConfirmIneligibleDialogProps) => {
  const { t } = useTranslation('@cibo/programs/ConfirmDeleteEnagagementDialog')
  const deleteEngagement = useDeleteProgramEngagement()
  useEffect(deleteEngagement.reset, [open])
  const resourceIds = reasons && (pluck('id', reasons) as string[])
  const engagement = useProgramEngagement({ id: engagementId })
  const program = useProgram({ programId: engagement.data?.programId })

  if (program.isLoading) {
    return <Loading />
  }

  const handleConfirm = () => {
    !!engagementId &&
      deleteEngagement
        .mutateAsync({ id: engagementId })
        .then(() => onConfirm())
        .catch(error => {
          logRequestError(error, { engagementId })
        })
  }

  return (
    <ResponsiveDialog
      maxWidth="sm"
      open={open}
      onClose={deleteEngagement.isLoading ? () => undefined : onClose}
      fullWidth
    >
      <DialogTitle sx={{ minWidth: 384 }}>{t('title', { count: resourceIds?.length })}</DialogTitle>

      <DialogContent>
        <Typography>
          {t('body', {
            count: resourceIds?.length,
            engageType: t('engageType', {
              context: program.data?.engageType,
              ns: '@cibo/ui',
            }).toLowerCase(),
          })}
        </Typography>

        <FieldReasonsTable reasons={reasons} />

        <Collapse in={!!deleteEngagement.isError}>
          <Alert severity="error">
            {t('errorDeletingEngagement', {
              engageType: t('engageType', {
                context: program.data?.engageType,
                ns: '@cibo/ui',
              }).toLowerCase(),
            })}
          </Alert>
        </Collapse>
      </DialogContent>

      <DialogActions>
        <Button color="secondary" disabled={deleteEngagement.isLoading} onClick={onClose}>
          {t('keepEditing', { ns: '@cibo/programs/ConfirmIneligibleDialog' })}
        </Button>
        <LoadingButton
          variant="contained"
          loading={deleteEngagement.isLoading}
          onClick={handleConfirm}
        >
          {t('confirm', { ns: '@cibo/programs/ConfirmIneligibleDialog' })}
        </LoadingButton>
      </DialogActions>
    </ResponsiveDialog>
  )
}
